// CP_06 fix: every stakeholder app previously read/wrote the SAME
// localStorage keys for session/identity state (token, centerInfo, ...), so
// logging into a second stakeholder app in another browser tab silently
// overwrote the first tab's session — the next API call in that tab then
// used the wrong app's token and failed.
//
// This wrapper prefixes just those keys by which app is currently running,
// resolved from the URL at call time (not from which file is calling it) —
// the same trick already used by the inactivity-timeout and 401-redirect
// fixes. Each app's session now lives in its own localStorage slot.
//
// Scratch/UI keys (code, id, reference, dropOff, stats, ...) are
// intentionally left unscoped for now: they are short-lived, overwritten
// immediately before being read again within a single flow, and namespacing
// them is a separate, lower-priority pass.

// Longest/most specific prefix first: "/dashboards" (collections) is itself
// a prefix of "/dashboard" (aggregation), so it must be checked first.
const APP_PREFIX_BY_PATH = [
  ["/dashboards", "collection"],
  ["/dashboard", "aggregation"],
  ["/collections", "collection"],
  ["/collection", "collection"],
  ["/aggregation", "aggregation"],
  ["/factory", "factory"],
  ["/admin", "superAdmin"],
  ["/drivers", "driver"],
];

const currentPrefix = () => {
  const path = window.location.pathname;
  const match = APP_PREFIX_BY_PATH.find(([prefix]) => path.startsWith(prefix));
  return match ? match[1] : "shared";
};

const SCOPED_KEYS = new Set([
  "token",
  "centerInfo",
  "factoryInfo",
  "center",
  "state_id",
  "manual_pricing",
  "use_state_pricing",
  "expireTime",
]);

const scopedKey = (key) => (SCOPED_KEYS.has(key) ? `${currentPrefix()}_${key}` : key);

export const storage = {
  getItem: (key) => localStorage.getItem(scopedKey(key)),
  setItem: (key, value) => localStorage.setItem(scopedKey(key), value),
  removeItem: (key) => localStorage.removeItem(scopedKey(key)),
  // Replaces a bare localStorage.clear() on logout / session expiry:
  // clearing everything would also wipe every OTHER stakeholder app's
  // session open in the same browser, which is the exact bug this file
  // exists to fix. Only this app's own scoped keys are removed.
  clearScoped: () => {
    const prefix = currentPrefix();
    SCOPED_KEYS.forEach((key) => localStorage.removeItem(`${prefix}_${key}`));
  },
};

// The actual storage key this app's token lives under right now — for code
// that needs to recognize it by name rather than through storage.getItem,
// e.g. matching a cross-tab `storage` event's event.key.
export const scopedTokenKey = () => scopedKey("token");
